import {
  INTRANET_REACTION_EMOJI,
  INTRANET_REACTION_KINDS,
  INTRANET_REACTION_LABEL,
  INTRANET_REACTION_QUICK_KINDS,
  type IntranetReactionKind,
} from '@gb-ticket/shared-types';
import { SearchIcon } from 'lucide-react';
import { useLayoutEffect, useMemo, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

const CHOICE =
  'grid size-8 shrink-0 place-items-center rounded-full text-base hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';
const RISE = 'animate-in fade-in-0 slide-in-from-bottom-1 duration-150 motion-reduce:animate-none';
const BAR_H = 40;
const EXTRA_H = 156;
const GAP = 2;
const EDGE = 12;

function anchorRect(anchor?: RefObject<HTMLElement | null>): DOMRect | null {
  const root = anchor?.current;
  if (!root) {
    return null;
  }
  const button = root.matches('button') ? root : root.querySelector('button');
  return (button ?? root).getBoundingClientRect();
}

export function isIntranetReactionOverlay(target: Node): boolean {
  const el = target instanceof Element ? target : target.parentElement;
  return Boolean(el?.closest('[data-intranet-reaction-picker]'));
}

/** Emoji bar and extra panel float on the page, not inside the contribution frame. */
export function IntranetReactionPicker({
  current,
  onPick,
  onPanelHoverChange,
  anchor,
}: {
  current: IntranetReactionKind | null;
  onPick: (kind: IntranetReactionKind) => void;
  onPanelHoverChange?: (inside: boolean) => void;
  anchor?: RefObject<HTMLElement | null>;
}) {
  const [more, setMore] = useState(false);
  const [query, setQuery] = useState('');
  const [box, setBox] = useState<{ left: number; top: number; placement: 'above' | 'below' }>({
    left: 0,
    top: 0,
    placement: 'above',
  });
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return INTRANET_REACTION_KINDS;
    }
    return INTRANET_REACTION_KINDS.filter((kind) => {
      return (
        INTRANET_REACTION_LABEL[kind].toLowerCase().includes(needle) ||
        INTRANET_REACTION_EMOJI[kind].includes(query.trim())
      );
    });
  }, [query]);

  function closeMore() {
    setMore(false);
    setQuery('');
  }

  useLayoutEffect(() => {
    function place() {
      const rect = anchorRect(anchor);
      const extra = more ? EXTRA_H + 2 : 0;
      const stack = BAR_H + extra;
      if (!rect) {
        setBox({ left: EDGE, top: EDGE, placement: 'above' });
        return;
      }
      const spaceAbove = rect.top - EDGE;
      const spaceBelow = window.innerHeight - rect.bottom - EDGE;
      const placement = spaceAbove >= stack || spaceAbove >= spaceBelow ? 'above' : 'below';
      const width = 240;
      const left = Math.max(
        EDGE,
        Math.min(rect.left, Math.max(EDGE, window.innerWidth - width - EDGE)),
      );
      const top =
        placement === 'above' ? Math.max(EDGE, rect.top - GAP - stack) : rect.bottom + GAP;
      setBox({ left, top, placement });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, more]);

  const extraPanel = more ? (
    <div
      role="dialog"
      aria-label="Weitere Reaktionen"
      className={cn('w-[18rem] max-w-[calc(100vw-1.5rem)]', RISE)}
    >
      <div className="rounded-xl border bg-popover p-2 shadow-lg">
        <label className="relative block">
          <SearchIcon
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            aria-label="Reaktionen suchen"
            placeholder="Suchen"
            value={query}
            autoFocus
            className="h-9 w-full rounded-md border bg-background pr-3 pl-8 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <p className="px-1 pt-2 pb-1 text-xs text-muted-foreground">Häufig verwendet</p>
        {shown.length === 0 ? (
          <p className="px-1 py-2 text-sm text-muted-foreground">Keine Treffer</p>
        ) : (
          <div className="grid grid-cols-6 gap-0.5">
            {shown.map((kind) => (
              <ReactionChoice key={kind} kind={kind} pressed={current === kind} onPick={onPick} />
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  return createPortal(
    <div
      data-intranet-reaction-picker=""
      className="fixed z-50 flex flex-col gap-0.5"
      style={{ left: box.left, top: box.top }}
      onClick={(event) => event.stopPropagation()}
      onMouseEnter={() => onPanelHoverChange?.(true)}
      onMouseLeave={() => onPanelHoverChange?.(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && more) {
          event.stopPropagation();
          closeMore();
        }
      }}
    >
      {box.placement === 'above' ? extraPanel : null}
      <div
        role="toolbar"
        aria-label="Reaktion wählen"
        className={cn(
          'flex w-max flex-nowrap items-center gap-0.5 rounded-full border bg-popover p-0.5 shadow-md',
          RISE,
        )}
      >
        {INTRANET_REACTION_QUICK_KINDS.map((kind) => (
          <ReactionChoice key={kind} kind={kind} pressed={current === kind} onPick={onPick} />
        ))}
        <button
          type="button"
          aria-label="Weitere Reaktionen"
          aria-haspopup="dialog"
          aria-expanded={more}
          className={CHOICE}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (more) {
              closeMore();
              return;
            }
            setMore(true);
          }}
        >
          +
        </button>
      </div>
      {box.placement === 'below' ? extraPanel : null}
    </div>,
    document.body,
  );
}

function ReactionChoice({
  kind,
  pressed,
  onPick,
}: {
  kind: IntranetReactionKind;
  pressed: boolean;
  onPick: (kind: IntranetReactionKind) => void;
}) {
  return (
    <button
      type="button"
      aria-label={INTRANET_REACTION_LABEL[kind]}
      aria-pressed={pressed}
      className={CHOICE}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onPick(kind);
      }}
    >
      {INTRANET_REACTION_EMOJI[kind]}
    </button>
  );
}
